import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { useCan } from '@/shared/session/profiles';
import {
  type PriceList,
  STATUS_LABELS,
  type Supplier,
  TARIFF_ACCEPT,
  tariffDate,
} from '@/shared/tariffs/types';
import { useCreatePriceList, useHistory, useUploadVersion } from '@/shared/tariffs/useTariffs';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';
import { PlusIcon, UploadIcon } from '@/shared/ui/icons';
import { formatBytes } from '@/shared/utils/format';

const COUNT = new Intl.NumberFormat('es-ES');

/**
 * The tariffs of a supplier (contract v10.48): the version in force of each, the one on
 * its way for whoever manages them, and the history. A tariff is a series of versions,
 * never overwritten (ADR 0043 of the back): a quote made from one says where its price
 * came from.
 */
export function PriceListsCard({ supplier }: { supplier: Supplier }) {
  const manage = useCan('manage_documents');
  const [adding, setAdding] = useState(false);
  const lists = supplier.price_lists ?? [];

  return (
    <Card
      title="Tarifas"
      aside={
        manage ? (
          <Button
            variant="secondary"
            size="sm"
            icon={<PlusIcon size={14} />}
            onClick={() => setAdding(true)}
          >
            Nueva tarifa
          </Button>
        ) : undefined
      }
    >
      {lists.length === 0 ? (
        <p className="text-ink-3 text-[14px]">
          {manage
            ? 'Sube el Excel o el CSV tal como lo manda el proveedor: la plataforma reconoce las columnas.'
            : 'Este proveedor no tiene tarifas todavía.'}
        </p>
      ) : (
        <ul className="divide-line flex flex-col divide-y">
          {lists.map((list) => (
            <PriceListRow key={list.id} list={list} manage={manage} />
          ))}
        </ul>
      )}
      {adding ? <NewPriceListDialog supplier={supplier} onDone={() => setAdding(false)} /> : null}
    </Card>
  );
}

function PriceListRow({ list, manage }: { list: PriceList; manage: boolean }) {
  const navigate = useNavigate();
  const upload = useUploadVersion();
  const input = useRef<HTMLInputElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const history = useHistory(list.id, showHistory);
  const { current, pending } = list;
  const older = (history.data?.items ?? []).filter((each) => each.id !== current?.id);

  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="text-ink text-[14.5px] font-medium">{list.name}</p>
          {current ? (
            <p className="text-ink-2 text-[13.5px]">
              Vigente del {tariffDate(current.tariff_date)} ·{' '}
              {COUNT.format(current.items_count ?? 0)} artículos ·{' '}
              <Link className="link" to={`/documentos/tarifas/${current.id}`}>
                Ver artículos
              </Link>
            </p>
          ) : (
            <p className="text-ink-3 text-[13.5px]">Sin publicar todavía.</p>
          )}
        </div>
        {manage && !pending ? (
          <>
            <input
              ref={input}
              type="file"
              accept={TARIFF_ACCEPT}
              hidden
              aria-label={`Subir versión nueva de ${list.name}`}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                upload.mutate(
                  { priceListId: list.id, file },
                  { onSuccess: (version) => void navigate(`/documentos/tarifas/${version.id}`) },
                );
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              icon={<UploadIcon size={14} />}
              disabled={upload.isPending}
              onClick={() => input.current?.click()}
            >
              {upload.isPending ? 'Subiendo…' : 'Subir versión nueva'}
            </Button>
          </>
        ) : null}
      </div>

      {manage && pending ? (
        <div className="bg-paper-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-3 py-2 text-[13.5px]">
          <Badge tone={pending.status === 'failed' || pending.stuck ? 'danger' : 'warn'}>
            {pending.stuck ? 'Atascada' : STATUS_LABELS[pending.status]}
          </Badge>
          <span className="text-ink-2 min-w-0 truncate">{pending.filename}</span>
          <Link className="link ml-auto" to={`/documentos/tarifas/${pending.id}`}>
            Continuar
          </Link>
        </div>
      ) : null}

      {upload.isError ? (
        <ErrorMessage error={upload.error}>No se pudo subir la tarifa</ErrorMessage>
      ) : null}

      <div>
        <button
          type="button"
          className="text-ink-3 hover:text-ink text-[13px] underline underline-offset-2"
          aria-expanded={showHistory}
          onClick={() => setShowHistory((value) => !value)}
        >
          {showHistory ? 'Ocultar versiones anteriores' : 'Versiones anteriores'}
        </button>
        {showHistory && history.data ? (
          older.length === 0 ? (
            <p className="text-ink-3 mt-1 text-[13px]">No hay versiones anteriores.</p>
          ) : (
            <ul className="mt-1 flex flex-col gap-1 text-[13px]">
              {older.map((each) => (
                <li key={each.id} className="text-ink-2 flex flex-wrap gap-x-2">
                  <Link className="link" to={`/documentos/tarifas/${each.id}`}>
                    {each.filename}
                  </Link>
                  <span>
                    {STATUS_LABELS[each.status]}
                    {each.tariff_date ? ` · del ${tariffDate(each.tariff_date)}` : ''}
                    {each.items_count ? ` · ${COUNT.format(each.items_count)} artículos` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )
        ) : null}
        {showHistory && history.isError ? (
          <ErrorMessage error={history.error}>No se pudieron cargar las versiones</ErrorMessage>
        ) : null}
      </div>
    </li>
  );
}

/** A new tariff of the supplier with its first file: «Máquinas», «Accesorios», «General». */
function NewPriceListDialog({ supplier, onDone }: { supplier: Supplier; onDone: () => void }) {
  const navigate = useNavigate();
  const create = useCreatePriceList(supplier.id);
  const [name, setName] = useState((supplier.price_lists ?? []).length ? '' : 'Tarifa general');
  const [file, setFile] = useState<File | null>(null);

  return (
    <Dialog
      title={`Nueva tarifa de ${supplier.name}`}
      description="El Excel (.xlsx, .xlsm o .xls) o el CSV tal como lo manda el proveedor."
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button
            disabled={!name.trim() || !file || create.isPending}
            onClick={() => {
              if (!file) return;
              create.mutate(
                { name: name.trim(), file },
                {
                  onSuccess: (version) => {
                    onDone();
                    void navigate(`/documentos/tarifas/${version.id}`);
                  },
                },
              );
            }}
          >
            {create.isPending ? 'Subiendo…' : 'Subir'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Nombre de la tarifa"
          value={name}
          maxLength={120}
          onChange={(event) => setName(event.target.value)}
          hint="Si el proveedor manda varias (máquinas y accesorios), una por cada una."
        />
        <label className="flex flex-col gap-1 text-[13.5px]">
          <span className="text-ink-2 font-medium">Fichero</span>
          <input
            type="file"
            accept={TARIFF_ACCEPT}
            className="text-ink-2 text-[13.5px]"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          {file ? (
            <span className="text-ink-3 text-[12.5px]">
              {file.name} · {formatBytes(file.size)}
            </span>
          ) : null}
        </label>
        {create.isError ? (
          <ErrorMessage error={create.error}>No se pudo subir la tarifa</ErrorMessage>
        ) : null}
      </div>
    </Dialog>
  );
}
